import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import {
  Button,
  Group,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import React from 'react';

export type ApiKeyType =
  | 'all'
  | 'organization'
  | 'graph'
  | 'variant'
  | 'subgraph';

interface ApiKeyFilterProps<T extends string> {
  selectedValue: T | '';
  onValueChange: (value: T) => void;
  options: T[];
  buttonText: string;
  allOptionText: string;
  allOptionValue: T | '';
  valueLabels?: Record<string, string>;
}

/**
 * @description A generic filter component that can be used to filter by a given set of options.
 * @param selectedValue - The currently selected value.
 * @param onValueChange - The function to call when a value is selected.
 * @param options - The option of values to display.
 * @param buttonText - The text to display on the button.
 * @param allOptionText - The text to display for the "All" option.
 * @param allOptionValue - The value to use for the "All" option.
 * @param valueLabels - A mapping of option values to display text.
 */
const ApiKeyFilter = <T extends string>({
  selectedValue,
  onValueChange,
  options,
  buttonText,
  allOptionText,
  allOptionValue,
  valueLabels,
}: ApiKeyFilterProps<T>) => {
  const getDisplayText = () => {
    if (valueLabels && selectedValue in valueLabels) {
      return valueLabels[selectedValue];
    }
    return selectedValue || buttonText;
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="secondary"
        rightIcon={<IconChevronDown />}
      >
        {getDisplayText()}
      </MenuButton>
      <MenuContent>
        <MenuList>
          <MenuItem onClick={() => onValueChange(allOptionValue as T)}>
            {allOptionText}
          </MenuItem>
          {options
            .filter((option) => option !== allOptionValue)
            .map((option) => (
              <MenuItem key={option} onClick={() => onValueChange(option)}>
                {valueLabels?.[option] || option}
              </MenuItem>
            ))}
        </MenuList>
      </MenuContent>
    </Menu>
  );
};

interface ApiKeyFilterTabsProps {
  selectedType: ApiKeyType;
  onTypeChange: (type: ApiKeyType) => void;
  typeOptions: ApiKeyType[];
  selectedEntity?: string;
  onEntityChange?: (entity: string) => void;
  entityOptions?: string[];
}

const typeLabels: Record<ApiKeyType, string> = {
  all: 'Type',
  organization: 'Organization',
  graph: 'Graph',
  variant: 'Variant',
  subgraph: 'Subgraph',
};

export const ApiKeyFilterTabs = ({
  selectedType,
  onTypeChange,
  typeOptions,
  selectedEntity,
  onEntityChange,
  entityOptions,
}: ApiKeyFilterTabsProps) => {
  return (
    <Group gap={3}>
      {typeOptions && typeOptions.length > 0 && (
        <ApiKeyFilter
          selectedValue={selectedType}
          onValueChange={onTypeChange}
          options={typeOptions}
          buttonText="Type"
          allOptionText="All Types"
          allOptionValue="all"
          valueLabels={typeLabels}
        />
      )}

      {entityOptions && entityOptions.length > 0 && (
        <ApiKeyFilter
          selectedValue={selectedEntity || ''}
          onValueChange={onEntityChange || (() => {})}
          options={entityOptions}
          buttonText="Entity"
          allOptionText="All Entities"
          allOptionValue=""
        />
      )}
    </Group>
  );
};
